/**
 * Validação e cálculo de CNPJ Alfanumérico (CNPJ Alfa)
 *
 * Implementa a Instrução Normativa RFB NTCJ 2025.001 da Receita Federal
 * para validação de CNPJs alfanuméricos compostos por 12 caracteres (A-Z ou 0-9)
 * seguidos de 2 dígitos verificadores numéricos.
 *
 * Mantém compatibilidade total com CNPJs numéricos (formato antigo).
 *
 * @example
 * // CNPJ Numérico (formato antigo)
 * CNPJAlfa.isValid('11.222.333/0001-81'); // true
 *
 * // CNPJ Alfa (novo formato)
 * CNPJAlfa.isValid('12.ABC.345/01DE-35'); // true
 */
export class CNPJAlfa {
    /** Tamanho do CNPJ sem os dígitos verificadores */
    private static readonly tamanhoCNPJSemDV = 12;

    /** Regex para validar CNPJ base (12 caracteres alfanuméricos) */
    private static readonly regexCNPJSemDV = /^([A-Z\d]){12}$/;

    /** Regex para validar CNPJ completo (12 alfanuméricos + 2 dígitos) */
    private static readonly regexCNPJ = /^([A-Z\d]){12}(\d){2}$/;

    /** Regex para remover caracteres de máscara permitidos */
    private static readonly regexCaracteresMascara = /[./-]/g;

    /** Regex para identificar caracteres não permitidos */
    private static readonly regexCaracteresNaoPermitidos = /[^A-Z\d./-]/i;

    /**
     * Valor ASCII de '0' = 48
     * Usado para conversão: charCodeAt(char) - 48 = valor numérico equivalente
     */
    private static readonly valorBase = "0".charCodeAt(0);

    /**
     * Pesos para cálculo dos dígitos verificadores (distribuídos de 2 a 9)
     * - Para 1º DV (12 chars): índices [1..12] = pesos [5,4,3,2,9,8,7,6,5,4,3,2]
     * - Para 2º DV (13 chars): índices [0..12] = pesos [6,5,4,3,2,9,8,7,6,5,4,3,2]
     */
    private static readonly pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    /** CNPJ zerado (inválido) */
    private static readonly cnpjZerado = "00000000000000";

    /**
     * Valida se o CNPJ é válido (numérico ou alfanumérico)
     *
     * @param cnpj - CNPJ com ou sem máscara (ex: "12.ABC.345/01DE-35" ou "12ABC34501DE35")
     * @returns true se o CNPJ for válido, false caso contrário
     *
     * @example
     * CNPJAlfa.isValid('11.222.333/0001-81'); // true - CNPJ numérico válido
     * CNPJAlfa.isValid('12.ABC.345/01DE-35'); // true - CNPJ alfa válido
     * CNPJAlfa.isValid('12ABC34501DE99');      // false - DV incorreto
     * CNPJAlfa.isValid('00000000000000');      // false - CNPJ zerado
     */
    static isValid(cnpj: string): boolean {
        if (!cnpj) {
            return false;
        }

        // Rejeitar CNPJs com caracteres não permitidos
        if (this.regexCaracteresNaoPermitidos.test(cnpj)) {
            return false;
        }

        const cnpjSemMascara = this.removeMascaraCNPJ(cnpj);

        // Validar formato e rejeitar CNPJ zerado
        if (!this.regexCNPJ.test(cnpjSemMascara) || cnpjSemMascara === this.cnpjZerado) {
            return false;
        }

        // Extrair base e DV informado
        const dvInformado = cnpjSemMascara.substring(this.tamanhoCNPJSemDV);
        const cnpjBase = cnpjSemMascara.substring(0, this.tamanhoCNPJSemDV);

        try {
            const dvCalculado = this.calculaDV(cnpjBase);
            return dvInformado === dvCalculado;
        } catch {
            return false;
        }
    }

    /**
     * Calcula os dois dígitos verificadores do CNPJ Alfa
     *
     * Algoritmo conforme Instrução Normativa RFB NTCJ 2025.001:
     * 1. Para cada caractere: valor = ASCII - 48
     * 2. Distribuir pesos de 2 a 9 da direita para esquerda
     * 3. Calcular somatório de (valor × peso)
     * 4. DV = (11 - (somatório % 11)) se resto >= 2, senão DV = 0
     *
     * @param cnpj - Base do CNPJ com 12 caracteres (sem DV)
     * @returns String com os 2 dígitos verificadores
     *
     * @throws Error se o CNPJ base for inválido
     *
     * @example
     * CNPJAlfa.calculaDV('12ABC34501DE'); // '35'
     * CNPJAlfa.calculaDV('112223330001'); // '81'
     */
    static calculaDV(cnpj: string): string {
        if (this.regexCaracteresNaoPermitidos.test(cnpj)) {
            throw new Error("Não é possível calcular o DV pois o CNPJ contém caracteres inválidos");
        }

        const cnpjSemMascara = this.removeMascaraCNPJ(cnpj);

        if (!this.regexCNPJSemDV.test(cnpjSemMascara) ||
            cnpjSemMascara === this.cnpjZerado.substring(0, this.tamanhoCNPJSemDV)) {
            throw new Error("Não é possível calcular o DV pois o CNPJ fornecido é inválido");
        }

        // === Cálculo do 1º Dígito Verificador ===
        let somatorioDV1 = 0;
        let somatorioDV2 = 0;

        for (let i = 0; i < this.tamanhoCNPJSemDV; i++) {
            // Converter caractere para valor numérico (ASCII - 48)
            const asciiDigito = cnpjSemMascara.charCodeAt(i) - this.valorBase;

            // Multiplicar pelo peso correspondente
            somatorioDV1 += asciiDigito * this.pesosDV[i + 1];
            somatorioDV2 += asciiDigito * this.pesosDV[i];
        }

        // Calcular 1º DV: se resto < 2, DV = 0, senão DV = 11 - resto
        const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11);

        // === Cálculo do 2º Dígito Verificador ===
        // Incluir o 1º DV no cálculo
        somatorioDV2 += dv1 * this.pesosDV[this.tamanhoCNPJSemDV];

        // Calcular 2º DV
        const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11);

        return `${dv1}${dv2}`;
    }

    /**
     * Remove a máscara do CNPJ e converte para maiúsculas
     *
     * @param cnpj - CNPJ com ou sem máscara
     * @returns CNPJ sem máscara em maiúsculas
     *
     * @example
     * CNPJAlfa.removeMascaraCNPJ('12.abc.345/01de-35'); // '12ABC34501DE35'
     * CNPJAlfa.removeMascaraCNPJ('11.222.333/0001-81'); // '11222333000181'
     */
    static removeMascaraCNPJ(cnpj: string): string {
        return cnpj.replace(this.regexCaracteresMascara, "").toUpperCase();
    }

    /**
     * Formata CNPJ com máscara padrão: XX.XXX.XXX/XXXX-XX
     *
     * @param cnpj - CNPJ sem máscara
     * @returns CNPJ formatado ou string original se inválido
     *
     * @example
     * CNPJAlfa.formatarCNPJ('12ABC34501DE35'); // '12.ABC.345/01DE-35'
     * CNPJAlfa.formatarCNPJ('11222333000181'); // '11.222.333/0001-81'
     */
    static formatarCNPJ(cnpj: string): string {
        const cnpjLimpo = this.removeMascaraCNPJ(cnpj);

        if (cnpjLimpo.length !== 14) {
            return cnpj; // Retorna sem formatação se tamanho incorreto
        }

        return cnpjLimpo.replace(
            /^([A-Z\d]{2})([A-Z\d]{3})([A-Z\d]{3})([A-Z\d]{4})(\d{2})$/,
            '$1.$2.$3/$4-$5'
        );
    }

    /**
     * Verifica se o CNPJ é do tipo Alfa (contém letras)
     *
     * @param cnpj - CNPJ com ou sem máscara
     * @returns true se contiver letras (CNPJ Alfa)
     *
     * @example
     * CNPJAlfa.isCNPJAlfa('12ABC34501DE35'); // true
     * CNPJAlfa.isCNPJAlfa('11222333000181'); // false
     */
    static isCNPJAlfa(cnpj: string): boolean {
        const cnpjSemMascara = this.removeMascaraCNPJ(cnpj);
        return /[A-Z]/i.test(cnpjSemMascara);
    }

    /**
     * Verifica se o CNPJ é numérico (formato antigo)
     *
     * @param cnpj - CNPJ com ou sem máscara
     * @returns true se for apenas numérico
     *
     * @example
     * CNPJAlfa.isCNPJNumerico('11.222.333/0001-81'); // true
     * CNPJAlfa.isCNPJNumerico('12ABC34501DE35');     // false
     */
    static isCNPJNumerico(cnpj: string): boolean {
        const cnpjSemMascara = this.removeMascaraCNPJ(cnpj);
        return /^\d{14}$/.test(cnpjSemMascara);
    }
}
